import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, ChevronRight, Zap, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StepForm from './StepForm';

export default function ActionForm({ formData, onSubmit, isLatest }) {
  const { t } = useTranslation('chat');
  const [formValues, setFormValues] = useState(formData.prefilled || {});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isLatest) return;
      setIsSubmitting(true);
      await onSubmit(formValues);
      setIsSubmitting(false);
    },
    [formValues, onSubmit, isLatest]
  );

  const isWorkflow = formData.total_steps > 1;
  const stepLabel = isWorkflow
    ? `${t('step', 'Step')} ${formData.step + 1}/${formData.total_steps}`
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={!isLatest ? 'opacity-60' : undefined}
    >
      <Card className="ml-11 mt-2 py-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-border flex items-center gap-2">
          <div className="size-6 rounded-md bg-primary/10 flex items-center justify-center">
            <Zap className="size-3.5 text-primary" />
          </div>
          <span className="font-medium text-sm text-foreground">
            {formData.action_name || formData.action}
          </span>
          <Badge variant="secondary" className="text-[10px]">{formData.module}</Badge>
          {stepLabel && (
            <>
              <ChevronRight className="size-3.5 text-muted-foreground" />
              <Badge variant="outline" className="text-[10px] text-primary border-primary/30">
                {stepLabel}
              </Badge>
            </>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4">
          <StepForm
            fields={formData.fields || []}
            values={formValues}
            onChange={setFormValues}
            disabled={!isLatest || isSubmitting}
          />

          {isLatest && (
            <div className="mt-4 flex justify-end">
              <Button type="submit" disabled={isSubmitting} className="gap-2">
                {isSubmitting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
                {isSubmitting ? t('executing', 'Executing...') : t('execute', 'Execute')}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </motion.div>
  );
}
